import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../../../models'
import * as query from '../../../query'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key as FnKey } from '../key'

/**
 * Retrieves information about an entity. By default, the entity for this record
 *
 * Useful in subqueries that reference a dimension
 *
 * @param key Conveys which piece of information is desired, using
 * {@link GetModelInfo.Key}. Can also specify which entity is to be referenced.
 * See examples below for more details
 * @example
 * // Get the entity ID for this record
 * { '@_': 'id' }
 * // ->
 * 'ent:!space:!room'
 *
 * // Get the name of the entity for this record
 * { '@_': 'name' }
 * // ->
 * 'Room'
 *
 * // Get the level of the entity for this record
 * { '@_': 'level' }
 * // ->
 * 3
 *
 * // Get the level of a specific entity
 * { '@_': [ 'ent:!space:!campus', 'level' ] }
 * // ->
 * 0
 *
 * // Get the id of the parent entity for this record's entity
 * { '@_': [ 'parent', 'id' ] }
 * // ->
 * 'ent:!space:!floor'
 *
 * // Get the id of the child entity for this record's entity
 * { '@_': [ 'child', 'id' ] }
 * // ->
 * undefined
 *
 * // Get the id of the root entity for the dimension of this record
 * { '@_': [ 'root', 'id' ] }
 * // ->
 * 'ent:!space:!campus'
 *
 * // Get the id of the leaf entity for the dimension of this record
 * { '@_': [ 'leaf', 'id' ] }
 * // ->
 * 'ent:!space:!room'
 *
 * // Get the name of the root entity of the specified dimension
 * { '@_': [ ['dim::!space', 'root'], 'name' ] }
 * // ->
 * 'Campus'
 *
 * // Get the name of the leaf entity of the specified dimension
 * { '@_': [ ['dim::!space', 'leaf'], 'name' ] }
 * // ->
 * 'Room'
 */
export interface GetModelInfo<out T = any> {
  /** {@link GetModelInfo} */
  readonly [FnKey.GetModelInfo]:
    | GetModelInfo.Key<T>
    | [modelSelector: GetModelInfo.ModelSelector, infoKey: GetModelInfo.Key<T>]
}
export namespace GetModelInfo {
  export const isGetModelInfo: TypeChecker<GetModelInfo> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<GetModelInfo>({
        [FnKey.GetModelInfo]: TypeChecker.Factory.union(
          Key.isKey,
          TypeChecker.Factory.tuple(ModelSelector.isModelSelector, Key.isKey),
        ),
      }),
    ).usingAlias('Fn.GetModelInfo')
  export const assertIsGetModelInfo: TypeAsserter<GetModelInfo> =
    TypeAsserter.Factory.from(isGetModelInfo)

  /**
   * Determines which {@link query.Relation} are accepted here
   */
  const RELATION_CRITERIA = {
    singleton: true,
  } as const satisfies query.Relation.Where.Criteria

  /**
   * Determines which entity's information is being retrieved
   */
  export type ModelSelector =
    | models.ForRecord.Id
    | query.Relation.Where<typeof RELATION_CRITERIA>
    | [models.Dimension.Id, 'root' | 'leaf']
  export namespace ModelSelector {
    export const isModelSelector: TypeChecker<ModelSelector> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.union(
          models.ForRecord.Id.isId,
          query.Relation.Where.getTypeChecker(RELATION_CRITERIA),
        ),
      ).usingAlias('GetModelInfo.ModelSelector')
  }

  /**
   * Supported keys for {@link GetModelInfo}
   */
  const MODEL_INFO_KEYS = [
    'description',
    'id',
    'level',
    'name',
    'note',
  ] as const satisfies readonly globalThis.Key.In.Union<models.ForRecord>[]
  type KeyBase = (typeof MODEL_INFO_KEYS)[number]
  /**
   * Used for distributing over `K`
   *
   * NOTE: this supports any key in `K` that at least has a possibility of
   * producing the desired type. For example, if the desired type is
   * `'ent:!space:!room' | 'n/a'` then 'id', 'name', and 'description' are all allowed,
   * but 'level' is not, since there is no way for 'level' to produce
   * `'ent:!space:!room' | 'n/a'`
   */
  type KeyHelper<T = any, K extends KeyBase = KeyBase> = K extends any
    ? If.Sometimes.Extends<
        T,
        Generalize<IndexUnion<models.ForRecord, K>>,
        K,
        never
      >
    : never
  type PseudoKeyHelper<T = any> = T extends boolean
    ? 'isRoot' | 'isLeaf'
    : never
  /**
   * Supported keys of {@link models.Entity} that provide the desired type
   */
  export type Key<T = any> = KeyHelper<T> | PseudoKeyHelper<T>
  export namespace Key {
    export const isKey: TypeChecker<Key> =
      TypeChecker.Factory.oneOf(MODEL_INFO_KEYS)
  }

  export const HELPER: FnHelper<GetModelInfo> = {
    createNode: (fn, context) => {
      let model: FlattenUnion<models.ForRecord> | undefined
      let k: Key
      const arg = fn[FnKey.GetModelInfo]
      if (TypeChecker.isReadonlyArray(arg)) {
        if (query.Relation.isRelation(arg[0])) {
          // Can only get relatives when in the context of an entity
          models.Entity.Id.assertIsId(
            context.modelId,
            `Cannot use '${FnKey.GetModelInfo}' to get information about a relative when the query's 'target' is not an entity`,
          )
          model = context.schema.tryGetRelative(context.modelId, arg[0])
        } else if (TypeChecker.isReadonlyArray(arg[0])) {
          const [dimensionId, level] = arg[0]
          model =
            context.schema[
              level === 'leaf' ? 'getLeafEntityOf' : 'getRootEntityOf'
            ](dimensionId)
        } else {
          model = context.schema.tryGetModel(arg[0])
        }
        k = arg[1]
      } else {
        TypeAsserter.assertIsNotNullish(
          context.modelId,
          `Cannot use '${FnKey.GetModelInfo}' without a specified model ID when the query has no 'target'`,
        )
        model = context.schema.tryGetModel(context.modelId)
        k = arg
      }
      // Handle pseudo-keys
      if (k === 'isRoot') {
        return new expressionNode.ForValue(
          model &&
            models.Entity.Id.isId(model.id) &&
            context.schema.isRoot(model.id),
        )
      } else if (k === 'isLeaf') {
        return new expressionNode.ForValue(
          model &&
            models.Entity.Id.isId(model.id) &&
            context.schema.isLeaf(model.id),
        )
      }
      // Handle actual key
      return new expressionNode.ForValue(model?.[k])
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetModelInfo,
  }
}
